import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Save, User, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { signOut } from "@/lib/auth";
import { z } from "zod";

const profileSchema = z.object({
  name: z.string().min(2).max(100),
  phone_e164: z.string().optional().or(z.literal("")),
});

const Profile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [form, setForm] = useState({ name: "", phone_e164: "" });

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      setEmail(user.email || "");

      const { data, error } = await supabase.from("profiles").select("*").eq("id", user.id).single();
      if (error) throw error;
      setForm({ name: data.name, phone_e164: data.phone_e164 || "" });
    } catch (error: any) {
      toast.error(error.message);
      navigate("/app");
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const validated = profileSchema.parse(form);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("profiles").update({
        name: validated.name,
        phone_e164: validated.phone_e164 || null,
      }).eq("id", user.id);

      if (error) throw error;
      toast.success("Profile updated");
    } catch (err: any) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
      else toast.error(err.message || "Failed to update");
    } finally { setSaving(false); }
  };

  const handleLogout = async () => {
    const { error } = await signOut();
    if (error) toast.error(error.message);
    else { toast.success("Logged out"); navigate("/"); }
  };

  if (loading) {
    return <AppShell title="Profile"><div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell title="Profile">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><User className="h-5 w-5" /> Your profile</CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={email} disabled className="rounded-xl bg-muted/50" />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={100} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone number (optional)</Label>
                  <Input id="phone" type="tel" placeholder="+91 98765 43210" value={form.phone_e164} onChange={(e) => setForm({ ...form, phone_e164: e.target.value })} inputMode="tel" className="rounded-xl" />
                  <p className="text-xs text-muted-foreground">Used for WhatsApp reminders</p>
                </div>
                <Button type="submit" disabled={saving} className="w-full rounded-xl">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Saving..." : "Save profile"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-danger/20">
            <CardContent className="py-6">
              <Button variant="outline" onClick={handleLogout} className="w-full rounded-xl text-danger hover:text-danger border-danger/30 hover:bg-danger/5">
                <LogOut className="h-4 w-4 mr-2" />
                Log out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
};

export default Profile;
